import _ from 'underscore';
import { useEffect, useLayoutEffect, useState } from 'react';
import URI from 'urijs';
import queryString from 'query-string';

import { ACCOUNT_MODE } from '@bingads-webui-campaign/campaign-ui-common';
import { getLength } from '@bingads-webui-campaign/special-characters';
import { useIsUnifiedProduct } from '@bingads-webui-campaign-react/up-context';
import { getParsedIndexAndProperty } from '@bingads-webui-react/error-mapper';
import { getTimezoneI18nString } from '@bingads-webui-campaign-react/help';
import { CurrencyUtils } from '@bingads-webui-clientcenter/entity-utils';

import {
  AdType,
  OdataType,
  UpCampaignGoals,
  McaChannelTypes,
  TermsAndConditionLink,
  McaChannelMinBudgetPerDay,
  DAYS_OF_MONTH,
  CurrencyOptions,
  MediaType,
  ACCOUNT_BUDGET_EXCEED_CODE
} from './constants';

export const getChannelGlyphColor = (channelType) => {
  const ChannelGlyphColorMap = {
    Facebook: '#3b5998',
    Twitter: '#1da1f2',
    LinkedIn: '#0a66c2',
  };
  return ChannelGlyphColorMap[channelType] || '';
};

export const getChannelGlyphName = (channelType) => {
  const channelGlyphNameMap = {
    Instagram: 'ba-InstagramLogo',
    Facebook: 'ba-FacebookLogo',
    Twitter: 'ba-TwitterLogo',
    LinkedIn: 'ba-LinkedInLogo',
  };
  return channelGlyphNameMap[channelType] || 'ba-ErrorBadge';
};

export function getCampaignGoals(goal) {
  if (!_.isEmpty(goal)) {
    return goal.split(',').map(g => g.trim());
  }
  return [];
}

const areSameDay = (d1, d2) => (
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate()
);

/**
 * Convert ISO string to UTC time for the day, ignoring the
 * time.
 * @param {string} isoTime time represented as Iso string.
 * @returns {number} UTC time in ms.
 * (i.e. no of ms since jan1, 1970,January 1, 1970, 00:00:00 UTC.)
 */
export const ConvertDateToUTCDay = (isoTime) => {
  const date = new Date(isoTime);

  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  );
};

/**
 * Check if the provided PST time is expired. (Used mainly in coupon
 * scenarios where coupon expiry time is always in PST without timezone info).
 * @param {string} expiryDatePST time value in PST without timezone
 * @returns {bool} If the time is expired per local time.
 */
export const HasDateExpiredInPST = (expiryDatePST) => {
  const localNow = new Date();
  const utcNow = localNow.getTime();
  // Expiry date is in PST
  const localExpiry = new Date(`${expiryDatePST}-08:00`);
  const utcExpiry = localExpiry.getTime();

  return utcNow > utcExpiry;
};

/**
 * Fills missing data in daterange with just date values for day
 * granularity. It returns an array of metric objects.
 * Output of this will be given to highchart to draw chart
 * for the specified daterange.
 * @param {Array} metricData list of metric objects.
 * @param {Object} chartDateRange daterange of chart.
 * @returns {Array} Array including proivided metric data and
 *  date values for days with missing data.
 */

export const FillEmptyDatesInRange = (metricData, chartDateRange) => {
  if (_.isEmpty(metricData)) {
    return [];
  }
  const chartStart = new Date(chartDateRange.start);
  const chartEnd = new Date(chartDateRange.end);

  let i = 0;
  const result = [];
  while (chartStart <= chartEnd) {
    const metricDate = (i < metricData.length) ?
      new Date(metricData[i].Date) : null;
    if (metricDate && areSameDay(metricDate, chartStart)) {
      // use metric data
      result.push({
        ...metricData[i],
        Date: ConvertDateToUTCDay(chartStart.toISOString()),
      });
      i += 1;
    } else {
      // Fill empty data.
      result.push({
        Date: ConvertDateToUTCDay(chartStart.toISOString()),
      });
    }
    chartStart.setDate(chartStart.getDate() + 1);
  }
  return result;
};

export const FillEmptyDatesInRangeForAllSocial = (metricData, chartDateRange) => {
  if (_.isEmpty(metricData)) {
    return [];
  }
  const chartStart = new Date(chartDateRange.start);
  const chartEnd = new Date(chartDateRange.end);
  metricData.sort((m1, m2) => ((m1.Date > m2.Date) ? 1 : -1));

  let i = 0;
  const result = [];
  let hasData = false;
  while (chartStart <= chartEnd) {
    let foundMatch = false;
    let metricDate = (i < metricData.length) ?
      new Date(metricData[i].Date) : null;
    while (metricDate && areSameDay(metricDate, chartStart)) {
      hasData = true;
      foundMatch = true;
      result.push({
        ...metricData[i],
        Date: ConvertDateToUTCDay(chartStart.toISOString()),
      });
      i += 1;
      metricDate = (i < metricData.length) ?
        new Date(metricData[i].Date) : null;
    }
    if (!foundMatch) {
      result.push({
        Date: ConvertDateToUTCDay(chartStart.toISOString()),
      });
    }
    chartStart.setDate(chartStart.getDate() + 1);
  }
  return hasData ? result : [];
};

export const fillEmptyDates = (metricData, granularityDays) => {
  if (_.isEmpty(metricData)) {
    return [];
  }

  const date = new Date(_.first(metricData).MetricDate);
  let i = 0;
  const result = [];
  while (i < metricData.length) {
    const metricDate = new Date(metricData[i].MetricDate);
    if (!areSameDay(metricDate, date)) {
      result.push({ Date: Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) });
    } else {
      result.push({
        ...metricData[i],
        Date: Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
      });
      i += 1;
    }
    date.setDate(date.getDate() + granularityDays);
  }
  return result;
};

export const mergeDataPointsWithOffset = (leftDataPoints, rightDataPoints, offset) => {
  const mergedDataPoints = [];
  const limit = Math.max(leftDataPoints.length, rightDataPoints.length + offset);
  for (let i = 0; i < limit; i += 1) {
    let dataPoint = null;
    if (i < leftDataPoints.length) {
      dataPoint = leftDataPoints[i];
    }
    const offsetIndex = i - offset;
    if (offsetIndex >= 0
      && offsetIndex < rightDataPoints.length
      && rightDataPoints[offsetIndex] !== null) {
      dataPoint = dataPoint !== null ?
        dataPoint + rightDataPoints[offsetIndex] : rightDataPoints[offsetIndex];
    }
    mergedDataPoints.push(dataPoint);
  }
  return mergedDataPoints;
};

export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Aggregates individual properties in an array of objects
 * and returns a single object with accumulated values.
 * Eg. [{a:1, b:1}, {a:2, b:1}] --> {a:3, b:2}
 * @param {Array} objectList list of objects to be aggregated.
 * @returns {Object} accumulated values in a single object.
 */
export const AggregateValues = (objectList) => {
  const aggregated = _.reduce(objectList, (result, current) => {
    const accumulated = _.mapObject(current, (value, key) =>
      value + (result[key] || 0));
    return accumulated;
  });

  const aggregatedItem = aggregated || {};
  return aggregatedItem;
};

export const ComputeEngagementsMetricsData = metricsData => metricsData.map(metrics => ({
  ...metrics,
  Engagements: (metrics.Reactions || 0) + (metrics.Comments || 0) + (metrics.Shares || 0),
}));

export const AggregateMetricsData = metricData => (_.reduce(metricData, (memo, metricValues) => ({
  Impressions: memo.Impressions + metricValues.Impressions,
  Clicks: memo.Clicks + metricValues.Clicks,
  Spend: memo.Spend + metricValues.Spend,
  Conversions: memo.Conversions + metricValues.Conversions,
  Calls: memo.Calls + metricValues.Calls,
  Directions: memo.Directions + metricValues.Directions,
  Reactions: memo.Reactions + metricValues.Reactions,
  Comments: memo.Comments + metricValues.Comments,
  Shares: memo.Shares + metricValues.Shares,
  Engagements: memo.Engagements + metricValues.Engagements,
  Revenue: memo.Revenue + metricValues.Revenue,
}), {
  Impressions: 0,
  Clicks: 0,
  Spend: 0,
  Conversions: 0,
  Calls: 0,
  Directions: 0,
  Reactions: 0,
  Comments: 0,
  Shares: 0,
  Engagements: 0,
  Revenue: 0,
}));

export function getAdType(ad) {
  const odataType = ad && ad['@odata.type'];
  const odataAdTypeMap = {
    '#Model.SearchAd': AdType.SearchAd,
    '#Model.ImageAd': AdType.ImageAd,
    '#Model.VideoAd': AdType.VideoAd,
  };
  return odataAdTypeMap[odataType];
}

export function useWindowEvent(event, handler) {
  useEffect(() => {
    // initiate the event handler
    window.addEventListener(event, handler);

    // this will clean up the event at destruct
    return function cleanup() {
      window.removeEventListener(event, handler);
    };
  }, [event, handler]);
}

export function useWindowSize() {
  const [size, setSize] = useState({
    innerWidth: 0,
    innerHeight: 0,
  });
  useLayoutEffect(() => {
    function updateSize() {
      setSize({
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
      });
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
}

export const ComputeEngagementsMetricsForOrganicPost = (post) => {
  const activityType = post[OdataType].replace(/^#Model./g, '');

  const {
    Likes,
    Shares,
    Comments,
    Replies,
    Retweets,
  } = post;
  switch (activityType) {
    case 'FacebookPost':
      return {
        ...post,
        Engagements: (_.isNumber(Likes) ? Likes : 0) +
          (_.isNumber(Shares) ? Shares : 0) +
          (_.isNumber(Comments) ? Comments : 0),
      };
    case 'InstagramPost':
      return {
        ...post,
        Engagements: (_.isNumber(Likes) ? Likes : 0) +
          (_.isNumber(Comments) ? Comments : 0),
      };
    case 'TwitterPost':
      return {
        ...post,
        Engagements: (_.isNumber(Replies) ? Replies : 0) +
          (_.isNumber(Retweets) ? Retweets : 0),
      };
    default:
      return post;
  }
};

export function getIndexVersion() {
  const regex = /^[\d|\w]{8}$/;
  const matches = regex.exec(document.lastChild.nodeValue);

  return matches ? matches[0] : '';
}

export function getTimestamp() {
  return window.performance ? performance.now() : new Date().getTime();
}

export function getInlineErrorOfInput({
  errors,
  inputValue,
  field,
  schema,
  legacyI18n,
}) {
  if (!_.isEmpty(errors)) {
    return _.values(errors);
  }
  const filedSchema = schema.properties[field] || {};
  const stringLength = getLength(inputValue, filedSchema.distinguishWideChar);
  return filedSchema.maxLength && stringLength > filedSchema.maxLength ?
    [legacyI18n.getString('Validation_String_Max_Length', { maxlength: filedSchema.maxLength })] : [];
}

export function PluckPropertyFromResponse(response, property) {
  return _.chain(response)
    .pluck(property)
    .uniq()
    .value();
}

export function IsWebSiteInvalid(error) {
  if (error && error.responseJSON) {
    const errorCodes = PluckPropertyFromResponse(error.responseJSON, 'Code');
    const properties = PluckPropertyFromResponse(error.responseJSON, 'Property');
    return (_.contains(errorCodes, 'InvalidValue') || _.contains(errorCodes, 'InvalidWebsiteUrl'))
      && _.contains(properties, 'Website');
  }
  return false;
}

export function isExceedDailyBudgetError(error) {
  if (error && error.responseJSON) {
    const errorCodes = PluckPropertyFromResponse(error.responseJSON, 'Code');
    const properties = PluckPropertyFromResponse(error.responseJSON, 'Property');
    return _.contains(errorCodes, ACCOUNT_BUDGET_EXCEED_CODE) && _.contains(properties, 'Budget');
  }
  return false;
}

export function isFbPermissionError(error) {
  if (error && error.responseJSON) {
    const errorCodes = PluckPropertyFromResponse(error.responseJSON, 'Code');

    return _.contains(errorCodes, 'NeedAdminPermission') ||
      _.contains(errorCodes, 'NeedBusinessAdminPermission');
  }
  return false;
}

export function getUrlWithProtocol(website) {
  if (!website) {
    return '';
  }

  const site = website.trim();
  const uri = new URI(site);

  if (uri.protocol()) {
    return uri.toString().replace(/\/$/g, '');
  }

  return URI(site).protocol('https').toString().replace(/\/$/g, '');
}

export function getUpCampaignGoalOptions(campaignGoal) {
  const isGoalValid = !_.isEmpty(campaignGoal);
  const campaignGoalOptions = {
    IncreaseWebsiteVisit: isGoalValid
      && campaignGoal.indexOf(UpCampaignGoals.IncreaseWebsiteVisit.value) > -1,
    IncreaseCallToBusiness: isGoalValid
      && campaignGoal.indexOf(UpCampaignGoals.IncreaseCallToBusiness.value) > -1,
    IncreaseBusinessLocationVisit: isGoalValid
      && campaignGoal.indexOf(UpCampaignGoals.IncreaseBusinessLocationVisit.value) > -1,
  };
  return campaignGoalOptions;
}

export function getSortedFilteredCountries(countries, currencyCountriesMappings, currency) {
  const availableCountries = _.findWhere(
    currencyCountriesMappings,
    { currency }
  );
  const filteredCountries = availableCountries ?
    _.filter(
      countries,
      item => _.contains(availableCountries.countries, item.value.Code)
    ) : countries;
  // 'co' is a optional parameter for localeCompare function
  // a general name of variant collations for certain locales
  return _.pluck(filteredCountries.sort((item1, item2) =>
    item1.value.Name.localeCompare(item2.value.Name, 'co', { sensitivity: 'base' })), 'value');
}

export function getSortedFilteredCurrencies(currencies) {
  const supportedCurrencies = _.pluck(CurrencyOptions, 'key');
  const filtered = _.filter(
    currencies,
    currency => _.contains(supportedCurrencies, currency && currency.value && currency.value.Code)
  );
  const sorted = CurrencyUtils.sortCurrencies(_.pluck(filtered, 'value'));
  return sorted;
}

export function convertTimeZonesForCcForm(i18n, timeZones) {
  return _.map(timeZones, item => ({
    Value: item.id,
    Text: i18n.getString(getTimezoneI18nString(item.id)),
  }));
}

export function extractAccountDataForCcForm(account, tempName) {
  let accountName = '';
  let businessName = '';
  let timeZone = '';
  let address = {};
  let taxInfo = [];
  let taxCertification = null;
  let currency = '';
  if (account) {
    if (account.Name && account.Name.indexOf(tempName) !== 0) {
      accountName = account.Name;
    }
    if (account.BusinessAddress) {
      if (account.BusinessAddress.BusinessName
        && account.BusinessAddress.BusinessName.indexOf(tempName) !== 0) {
        businessName = account.BusinessAddress.BusinessName;
      }
      address = {
        City: account.BusinessAddress.City || '',
        Country: account.BusinessAddress.Country || '',
        Line1: account.BusinessAddress.Line1 || '',
        Line2: account.BusinessAddress.Line2 || '',
        PostalCode: account.BusinessAddress.PostalCode || '',
        StateOrProvince: account.BusinessAddress.StateOrProvince || '',
      };
    }
    if (account.TimeZone) {
      timeZone = account.TimeZone.toString(10);
    }
    if (account.TaxInformation) {
      taxInfo = account.TaxInformation;
    }
    if (account.TaxCertification) {
      taxCertification = account.TaxCertification;
    }
    if (account.CurrencyType) {
      currency = account.CurrencyType;
    }
  }

  return {
    accountName, businessName, timeZone, address, taxInfo, taxCertification, currency,
  };
}

export function extractCustomerDataForCcForm(customer, tempName) {
  let customerName = '';
  let phoneNumber = '';
  if (customer) {
    if (customer.Name && customer.Name.indexOf(tempName) !== 0) {
      customerName = customer.Name;
    }
    if (customer.BusinessPhone) {
      phoneNumber = customer.BusinessPhone;
    }
  }

  return {
    customerName, phoneNumber,
  };
}

export function useApplicationName(forceUpContent = false) {
  const isUnifiedProduct = useIsUnifiedProduct();
  return (isUnifiedProduct || forceUpContent) ? 'Microsoft Advertising' : 'Digital Marketing Center';
}

export function useUpHelpLink(dmcQuery, upQuery) {
  const isUnifiedProduct = useIsUnifiedProduct();
  return isUnifiedProduct ? upQuery : dmcQuery;
}

export function getUpIsSearchOnly(ServingChannels) {
  if (_.contains(ServingChannels, McaChannelTypes.Facebook)) {
    return false;
  }
  return true;
}

export function getUpServingPlatforms(ServingChannels, i18n) {
  if (_.contains(ServingChannels, McaChannelTypes.Facebook) &&
    _.contains(ServingChannels, McaChannelTypes.GoogleAds) &&
    _.contains(ServingChannels, McaChannelTypes.MicrosoftAdvertising)) {
    return i18n.getString(_TL_('Microsoft Search Network, Google, Facebook, Instagram'));
  } else if (_.contains(ServingChannels, McaChannelTypes.Facebook) &&
    _.contains(ServingChannels, McaChannelTypes.MicrosoftAdvertising)) {
    return i18n.getString(_TL_('Microsoft Search Network, Facebook, Instagram'));
  } else if (_.contains(ServingChannels, McaChannelTypes.GoogleAds) &&
    _.contains(ServingChannels, McaChannelTypes.MicrosoftAdvertising)) {
    return i18n.getString(_TL_('Microsoft Search Network, Google'));
  }
  return i18n.getString(_TL_('Microsoft Search Network'));
}

export function getUpMinBudgetPerDay(servingChannels) {
  let budget = 0;
  if (_.contains(servingChannels, McaChannelTypes.Facebook)) {
    budget += McaChannelMinBudgetPerDay[McaChannelTypes.Facebook];
  }
  if (_.contains(servingChannels, McaChannelTypes.GoogleAds)) {
    budget += McaChannelMinBudgetPerDay[McaChannelTypes.GoogleAds];
  }
  if (_.contains(servingChannels, McaChannelTypes.MicrosoftAdvertising)) {
    budget += McaChannelMinBudgetPerDay[McaChannelTypes.MicrosoftAdvertising];
  }
  return budget;
}

export function getDMCServingPlatforms(
  ServingChannels,
  SocialProfiles,
  isCustomerFlaggedForDMCBWTIntegration,
  i18n
) {
  if (SocialProfiles && SocialProfiles.length > 0) {
    return i18n.getString(_TL_('Microsoft Search Network, Google, Facebook, Instagram'));
  } else if (isCustomerFlaggedForDMCBWTIntegration
    && !_.contains(ServingChannels, McaChannelTypes.GoogleAds)) {
    return i18n.getString(_TL_('Microsoft Search Network'));
  }
  return i18n.getString(_TL_('Microsoft Search Network, Google'));
}

export function getTrackingInfo() {
  const locationHref = window.location.href;
  const options = queryString.parseUrl(locationHref, { parseFragmentIdentifier: true });
  return {
    trackingId: options.query.t_id,
    sourceType: options.query.s_cid,
  };
}

export function getIsCampaignUrlChanged(campaignWebsite, website) {
  const formatUrl = (url) => {
    let newUrl = (url || '').toLowerCase();
    if (newUrl[newUrl.length - 1] === '/') {
      newUrl = newUrl.substring(0, newUrl.length - 1);
    }
    return newUrl.replace(/^https?:\/\//, '');
  };

  const campaignUrl = formatUrl(campaignWebsite);
  const url = formatUrl(website);

  return campaignUrl !== url;
}

export function getErrorsArray(response) {
  let errors = [];
  if (_.isArray(response)) {
    errors = response;
  } else if (_.isObject(response)) {
    if (response['@odata.context'] && _.isArray(response.value)) {
      errors = response.value;
    } else if (_.isArray(response.responseJSON)) {
      errors = response.responseJSON;
    } else if (_.isObject(response.responseJSON) && _.isArray(response.responseJSON.Errors)) {
      errors = response.responseJSON.Errors;
    }
  }
  return errors;
}

export function updateErrorWithClientIdOrErrMsgObj(payload, response, requiredProperty = null) {
  let payloadWithClientIdOrErrMsgObj = [];
  if (_.isArray(payload)) {
    payloadWithClientIdOrErrMsgObj = payload;
  } else if (_.isObject) {
    payloadWithClientIdOrErrMsgObj = [payload];
  }

  const errors = getErrorsArray(response);
  const errorWithClientIdOrErrMsgObj = _.map(errors, (error) => {
    const { index, property } = getParsedIndexAndProperty(error.Property);
    if (index && index < payloadWithClientIdOrErrMsgObj.length
      && (!requiredProperty || requiredProperty === property)) {
      const { ClientId, ErrMsgObj } = payloadWithClientIdOrErrMsgObj[index] || {};
      return {
        ...error,
        ClientId,
        ErrMsgObj,
      };
    }
    return error;
  });

  return errorWithClientIdOrErrMsgObj;
}

export const getUpTermsAndConditionLink = (locale) => {
  let currentLocale = 'en-us';
  if (_.isString(locale) && _.has(TermsAndConditionLink, locale.toLowerCase())) {
    currentLocale = locale.toLowerCase();
  }
  return TermsAndConditionLink[currentLocale];
};

export const getDatePickerStrings = i18n => ({
  months: [
    i18n.getString(_TL_('January')),
    i18n.getString(_TL_('February')),
    i18n.getString(_TL_('March')),
    i18n.getString(_TL_('April')),
    i18n.getString(_TL_('May')),
    i18n.getString(_TL_('June')),
    i18n.getString(_TL_('July')),
    i18n.getString(_TL_('August')),
    i18n.getString(_TL_('September')),
    i18n.getString(_TL_('October')),
    i18n.getString(_TL_('November')),
    i18n.getString(_TL_('December')),
  ],

  shortMonths: [
    i18n.getString(_TL_('Jan')),
    i18n.getString(_TL_('Feb')),
    i18n.getString(_TL_('Mar')),
    i18n.getString(_TL_('Apr')),
    i18n.getString(_TL_('May')),
    i18n.getString(_TL_('Jun')),
    i18n.getString(_TL_('Jul')),
    i18n.getString(_TL_('Aug')),
    i18n.getString(_TL_('Sep')),
    i18n.getString(_TL_('Oct')),
    i18n.getString(_TL_('Nov')),
    i18n.getString(_TL_('Dec')),
  ],

  days: [
    i18n.getString(_TL_('Sunday')),
    i18n.getString(_TL_('Monday')),
    i18n.getString(_TL_('Tuesday')),
    i18n.getString(_TL_('Wednesday')),
    i18n.getString(_TL_('Thursday')),
    i18n.getString(_TL_('Friday')),
    i18n.getString(_TL_('Saturday')),
  ],

  // likezh: These short day initials are not used. Left en-US defaults from Fluent UI.
  // https://github.com/microsoft/fluentui/blob/4f907a837f3b85923178b611e4fbb0d9ac222dda/packages/date-time-utilities/src/dateFormatting/dateFormatting.defaults.ts#L55
  shortDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],

  goToToday: i18n.getString(_TL_('Go to today')),
  prevMonthAriaLabel: i18n.getString(_TL_('Go to previous month')),
  nextMonthAriaLabel: i18n.getString(_TL_('Go to next month')),
  prevYearAriaLabel: i18n.getString(_TL_('Go to previous year')),
  nextYearAriaLabel: i18n.getString(_TL_('Go to next year')),
  closeButtonAriaLabel: i18n.getString(_TL_('Close date picker')),

  isRequiredErrorMessage: i18n.getString(_TL_('Start date is required.')),
  invalidInputErrorMessage: i18n.getString(_TL_('Invalid date format.')),
});

export const ifUPAccountExist = (serverContext = {}) =>
  serverContext && serverContext.CurrentAccount &&
  `${serverContext.CurrentAccount.AccountMode}` === `${ACCOUNT_MODE.UNIFIED_SMART}`;

export function isBusinessNameEditorialError(err = []) {
  const locationErr = _.find(err, info => info.McaEntityType === 'LocationAdExtension');
  if (locationErr && !_.isEmpty(locationErr.EditorialRejectReason)) {
    return !!_.find(locationErr.EditorialRejectReason, r => r.ViolatingLocation === 'LocationAdExtensionBusinessName');
  }
  return false;
}

export const getCampaignPageUrl = (serverContext, page) => {
  const {
    CurrentCustomer,
    CurrentAccount,
    CurrentUser,
    Urls,
  } = serverContext || {};
  const { Id: customerId } = CurrentCustomer || {};
  const { Id: accountId } = CurrentAccount || {};
  const { Id: userId } = CurrentUser || {};
  const { CampaignAzureDomain } = Urls || {};
  const host = CampaignAzureDomain || window.location.origin;
  return `${host}/campaign/vnext/${page}?cid=${customerId}&aid=${accountId}&uid=${userId}`;
};

export const getMonthlyBudget = dailyBudget => Math.floor(DAYS_OF_MONTH * dailyBudget);

export function getDailyBudget(monthlyBudget) {
  const dailyBudget = monthlyBudget / DAYS_OF_MONTH;
  return Number.parseFloat(dailyBudget.toFixed(2));
}

export const getMediaType = (type = '') => {
  if (type.startsWith('image/')) {
    return MediaType.Image;
  } else if (type.startsWith('video/')) {
    return MediaType.Video;
  }
  return MediaType.Unknown;
};

export const getExtension = (name = '') => {
  const index = _.lastIndexOf(name, '.');
  if (index > -1) {
    return name.substring(index).toLowerCase();
  }
  return null;
};

export function stringifyGoals(campaignGoalOptions) {
  const selectedCampaignGoalsArr = _.keys(_.pick(campaignGoalOptions, value => value));
  const goal = _.isEmpty(selectedCampaignGoalsArr) ? '' : selectedCampaignGoalsArr.join(', ');

  return goal;
}

// Account name should have at least three characters,
// if length is less than 3, add underscores (e.g. _a_)
export function getNameBeforeAccountActivate(name) {
  const length = (name && name.length) || 0;
  let result = name;
  if (name && length < 3) {
    result = `_${name}_`;
  }
  return result;
}
